/**
 * Segment OAuth popup for the source connector (`/integrations/segment_in`).
 *
 * Authorizing returns an authorization code, which the caller posts to
 * `POST /v1/environments/{id}/oauth2/segment` together with the workspace's API
 * credentials. The backend exchanges the code for a Segment install token and uses
 * it to manage the Customer.io destination in the reader's Segment workspace.
 */
import { openOAuthPopup, type OAuthOpenResponse } from './oauth-popup';
import { getSegmentOAuthClientId } from './segment-client-id';

const SEGMENT_PROVIDER = 'segment';
const SEGMENT_AUTHORIZATION_URL = 'https://id.segmentapis.com/oauth2/auth';
const SEGMENT_SCOPE = 'destination/customer.io';
const SEGMENT_POPUP_WIDTH = 640;
/**
 * Ceiling on the consent wait. Exported so tests advance the real value rather
 * than a copy of it — a restated literal turns a raised ceiling into a hung test
 * instead of a failing assertion.
 */
export const SEGMENT_CONSENT_TIMEOUT_MS = 300_000;

/**
 * The Segment OAuth app registers this path, not the `/oauth2/redirect` the other
 * providers use. Both are served by the same backend handler, so the difference is
 * only which URI Segment will accept.
 */
function getSegmentRedirectUri(): string {
  return `${window.location.origin}/torii/redirect.html`;
}

export function openSegmentOAuthPopup(): Promise<OAuthOpenResponse> {
  return openOAuthPopup({
    provider: SEGMENT_PROVIDER,
    baseUrl: SEGMENT_AUTHORIZATION_URL,
    clientId: getSegmentOAuthClientId(),
    scope: SEGMENT_SCOPE,
    redirectUri: getSegmentRedirectUri(),
    width: SEGMENT_POPUP_WIDTH,
    // Segment sends `id.segmentapis.com/oauth2/auth` on to `app.segment.com/authorize`
    // in a window we did not open, so the handle we hold closes while consent is
    // still running and cannot be used to end the wait.
    discardsPopupWindow: true,
    // Which means this clock also counts the time before the reader starts. Consent
    // itself is quick — ~10s on connect, ~8s on a verify that reuses a remembered
    // grant — but on production a disable made Segment prompt again and opened the
    // popup *behind* the main window. That attempt failed on a 90s ceiling because
    // the reader did not find the window in time; the same action completed as soon
    // as they did. So size this for finding a hidden window, not for consenting.
    // The cost is that an abandoned popup holds the connector busy until this
    // expires, because nothing separates "still consenting out of sight" from
    // "closed it and walked away". A cancel control on the connector is the real
    // answer and is page-level work.
    timeoutMs: SEGMENT_CONSENT_TIMEOUT_MS,
  });
}
